import React, { useEffect, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ActionIcon,
  Avatar,
  Center,
  Flex,
  Image,
  Loader,
  Modal,
  Popover,
  ScrollArea,
  Text,
  TextInput,
} from "@mantine/core";
import { GoPaperclip } from "react-icons/go";
import { IoChevronDownOutline } from "react-icons/io5";
import { useStyles } from "./style";
import { useSelector } from "react-redux";
import useRemoteSearch from "../../utils/useRemoteSearch";
import { searchStaffSkypoint } from "../../services/staff";
import {
  refreshSkypointBoard,
  updateSkypointProjectTask,
} from "../../services/skypointProject";
import { isStaff } from "../../http";
import { FiLink } from "react-icons/fi";
import { useDisclosure } from "@mantine/hooks";
import NewTaskModal from "../ProjectModal";
import { moreNote } from "../RemoteSearchSelect";

const TaskCard = ({ task }) => {
  const [opened, setOpened] = useState(false);
  // The staff to assign, searched on the server while the list is open (a whole list stopped
  // at the first rows, OD-1694)
  const {
    search: searchTerm,
    setSearch: setSearchTerm,
    result: staffResult,
    loading,
  } = useRemoteSearch(searchStaffSkypoint, { enabled: opened });
  const staffs = staffResult?.staffs || [];
  const staffNote = moreNote(staffs.length, staffResult?.count || 0, "staff");
  const { profile } = useSelector((state) => state?.profile);
  const [imgPreviewOpened, setImgPreviewOpened] = useState(false);
  const [taskModalOpened, { open: taskModalOpen, close: taskModalClose }] =
    useDisclosure(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grab" : "default",
  };

  const { classes } = useStyles();

  const getFileNameFromUrl = (url) => {
    if (!url) return "";

    try {
      let name = url.split("/").pop();

      // keep decoding until no encoded chars left
      let prev;
      do {
        prev = name;
        name = decodeURIComponent(name);
      } while (name !== prev);

      return name;
    } catch (e) {
      return url;
    }
  };

  const handleStaffClick = async (id) => {
    try {
      setOpened(false);
      const payload = {
        assignedTo: id || null,
        id: task?.id,
      };
      const res = await updateSkypointProjectTask(payload, refreshSkypointBoard);
    } catch (err) {}
  };

  // const handleImageDownload = async (url) => {
  //   try {
  //     const response = await fetch(url);
  //     const blob = await response.blob();

  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = downloadUrl;
  //     a.download = getFileNameFromUrl(url);
  //     document.body.appendChild(a);
  //     a.click();

  //     document.body.removeChild(a);
  //     window.URL.revokeObjectURL(downloadUrl);
  //   } catch (err) {
  //     console.error("Download failed", err);
  //   }
  // };

  const handleImagePreview = (e) => {
    setImgPreviewOpened(true);
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={classes.taskCard}
        {...attributes}
        {...listeners}
      >
        <p className={classes.taskCardTitle} onClick={taskModalOpen}>
          {task.title}
        </p>
        {/* <p className={classes.taskCardDescription} onClick={taskModalOpen}>
          {task.description}
        </p> */}
      <p
  className={classes.taskCardDescription}
  onClick={taskModalOpen}
  dangerouslySetInnerHTML={{ __html: task.description }}
/>
        {(task?.image || task?.link) && (
          <Flex className={classes.taskCardAttachedItems}>
            {task?.link && (
              <>
                <FiLink />
                <a
                  href={task?.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: task?.image ? "42%" : "90%",
                    marginRight: task?.image ? 10 : 0,
                  }}
                >
                  <Text className="fileName">{task?.link}</Text>
                </a>
              </>
            )}
            {task?.image && (
              <>
                <GoPaperclip />
                <Text
                  className="fileName"
                  w={task?.link ? "42%" : "90%"}
                  onClick={handleImagePreview}
                >
                  {getFileNameFromUrl(task?.image)}
                </Text>
              </>
            )}
          </Flex>
        )}
        <Flex className={classes.assignedTo}>
          <Text className="label">Assigned to:</Text>
          <Avatar size="sm" radius="xl" src={task?.assignedTo?.photo} />
          <Text className="assignedName">
            {task?.assignedTo ? task?.assignedTo?.name : "Unassigned"}
          </Text>
          {!isStaff(profile?.user?.role) && (
            <Popover
              opened={opened}
              onChange={setOpened}
              position="bottom-start"
              shadow="md"
              withinPortal
              classNames={{ dropdown: classes.popoverDropdown }}
            >
              <Popover.Target>
                <ActionIcon onClick={() => setOpened((o) => !o)}>
                  <IoChevronDownOutline size={20} color="#99A1AF" />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <TextInput
                  placeholder="Search staff"
                  m={10}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <ScrollArea h={220} p={5}>
                  {loading ? (
                    <Center>
                      <Loader size="sm" c="yellow" />
                    </Center>
                  ) : (
                    <>
                      <Flex
                        className={`${classes.staffItem} ${
                          !task?.assignedTo ? classes.assignedStaffItem : ""
                        }`}
                        onClick={() =>
                          task?.assignedTo && handleStaffClick(null)
                        }
                      >
                        <Avatar size="sm" radius="xl" src={null}></Avatar>
                        <p className="staffName">Unassigned</p>
                      </Flex>
                      {staffs.map((staff) => {
                        const isAssignedStaff =
                          task?.assignedTo?.id === staff?.id;
                        return (
                          <Flex
                            key={staff?.id}
                            className={`${classes.staffItem} ${
                              isAssignedStaff ? classes.assignedStaffItem : ""
                            }`}
                            onClick={() =>
                              !isAssignedStaff && handleStaffClick(staff.id)
                            }
                          >
                            <Avatar
                              size="sm"
                              radius="xl"
                              src={staff?.photo}
                            ></Avatar>
                            <p className="staffName">
                              {staff?.name}
                              {staff.id === profile?.user?.id ? " (Admin)" : ""}
                            </p>
                          </Flex>
                        );
                      })}
                      {staffNote && (
                        <Text size="xs" color="dimmed" p={10}>
                          {staffNote}
                        </Text>
                      )}
                    </>
                  )}
                </ScrollArea>
              </Popover.Dropdown>
            </Popover>
          )}
        </Flex>
      </div>
      <Modal
        opened={imgPreviewOpened}
        onClose={() => setImgPreviewOpened(false)}
        centered
        size="lg"
        zIndex={100000000}
        withCloseButton
        overlayOpacity={0.55}
      >
        <Center>
          <Image
            src={task?.image}
            alt=""
            fit="contain"
            style={{ maxHeight: "80vh" }}
          />
        </Center>
      </Modal>
      <NewTaskModal
        opened={taskModalOpened}
        onClose={() => taskModalClose()}
        mode="view"
        selectedTask={{ ...task }}
      />
    </>
  );
};

export default TaskCard;
